import * as React from "react";
import { Link } from "react-router-dom";
import {
  Space,
  Input,
  Popconfirm,
  Table,
  Skeleton,
  Spin,
  Form,
  message,
  Modal,
  Tag,
} from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import { PlusButton } from "components/PlusButton";
import { ListPageLayout } from "layouts";
import {
  DatasetApiCommunicator,
  formatFullDateFromTimeStamp,
  useAmplifyOrgSubscription,
} from "api";

import { useSearch } from "hooks";
import { addOrUpdateObjectInList } from "utils";

const { Search } = Input;

/**
 * DatasetListPage Component
 * 
 * Displays a list of all datasets with their current status and basic information.
 * Provides functionality for viewing, searching, creating, and deleting datasets.
 * 
 * Features:
 * - Sortable table with pagination
 * - Real-time search functionality
 * - Dataset creation modal
 * - Delete confirmation with popconfirm
 * - Real-time updates via Amplify subscriptions
 * - File upload progress tracking
 * - Download functionality for dataset files
 * 
 * @returns {JSX.Element} The dataset list page component
 */
export function DatasetListPage() {
  const [datasetApiCommunicator] = React.useState(new DatasetApiCommunicator());
  const [loading, setLoading] = React.useState(false);
  const [loadingMessage] = React.useState("Loading...");
  /** @type {ReturnType<typeof React.useState<import('models').Dataset[]>} */
  const [datasets, setDatasets] = React.useState([]);
  const [pendingDeleteId, setPendingDeleteId] = React.useState("");
  const [messageApi, contextHolder] = message.useMessage();

  const fetchPageData = React.useCallback(async () => {
    try {
      setLoading(true);
      const res = await datasetApiCommunicator.list();
      setDatasets(res.data.sort((a, b) => b.dateAdded - a.dateAdded));
    } catch (err) {
      messageApi.error(err.message);
    }
    setLoading(false);
  }, [messageApi, datasetApiCommunicator]);

  useAmplifyOrgSubscription((update) => {
    if (update.type === "dataset" && !loading) {
      const newDatasets = addOrUpdateObjectInList(
        datasets,
        update.data,
        "datasetId"
      );
      setDatasets(newDatasets.sort((a, b) => b.dateAdded - a.dateAdded));
    }
  });

  React.useEffect(() => {
    fetchPageData();
  }, [fetchPageData]);

  const {
    query: searchTerm,
    setQuery: setSearchTerm,
    loading: isSearching,
    results: searchResults,
    search,
  } = useSearch(
    true,
    500,
    "dataset",
    React.useCallback(
      (msg) => messageApi.error("Error searching: " + msg),
      [messageApi]
    )
  );

  const cancelDelete = React.useCallback(() => {
    setPendingDeleteId("");
  }, []);

  const confirmDelete = React.useCallback(
    async (datasetId) => {
      setPendingDeleteId("");
      setLoading(true);
      try {
        await datasetApiCommunicator.delete(datasetId);
        fetchPageData();
      } catch (err) {
        messageApi.error("Error deleting dataset " + err.message);
      }
      setLoading(false);
    },
    [datasetApiCommunicator, messageApi, fetchPageData]
  );

  const addDataset = React.useCallback(
    async (values) => {
      openAddDatasetModal(false);
      setLoading(true);

      try {
        await datasetApiCommunicator.create({
          datasetName: values.datasetName,
          datasetType: 'training',
          modelIds: [],
        });
        fetchPageData();
      } catch (err) {
        messageApi.error("Error creating dataset " + err.message);
      }
      setLoading(false);
    },
    [datasetApiCommunicator, messageApi, fetchPageData]
  );

  const handleSearchValueChange = (e) => {
    const { value: inputValue } = e.target;
    setSearchTerm(inputValue);
  };

  const columns = [
    {
      title: "Name",
      key: "DatasetName",
      render: (_, r) => (
        <Link to={`/datasets/${r.datasetId}`}>{r.datasetName}</Link>
      ),
    },
    {
      title: "Type",
      key: "datasetType",
      render: (_, r) => <div>{r.datasetType}</div>,
    },
    {
      title: "# Print Files",
      key: "numberModels",
      render: (_, r) => <div>{r.modelIds.length}</div>,
    },
    {
      title: "Tags",
      key: "tags",
      dataIndex: "tags",
      render: (tags) => (
        <>
          {tags.map((tag) => (
            <Tag color="blue" key={tag}>
              {tag}
            </Tag>
          ))}
        </>
      ),
    },
    {
      title: "Date",
      key: "dateAdded",
      render: (_, r) => <div>{formatFullDateFromTimeStamp(r.dateAdded)}</div>,
    },
    {
      title: "Action",
      key: "action",
      render: (_, r) => (
        <Space>
          <Popconfirm
            title="Delete this dataset?"
            description="Are you sure to delete this dataset? This action cannot be undone."
            open={r.datasetId === pendingDeleteId}
            onConfirm={() => confirmDelete(r.datasetId)}
            onCancel={cancelDelete}
            okText="Yes"
            cancelText="No"
          >
            <DeleteOutlined onClick={() => setPendingDeleteId(r.datasetId)} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const [addDatasetModalOpen, openAddDatasetModal] = React.useState(false);

  return (
    <ListPageLayout>
      {contextHolder}
      <Space
        direction="vertical"
        size="middle"
        style={{
          display: "flex",
        }}
      >
        {loading ? (
          <span>
            <Spin /> {loadingMessage}
          </span>
        ) : (
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <Space style={{ display: "flex", alignItems: "center" }}>
              <h3 style={{ paddingLeft: "16px" }}>Datasets</h3>
              <PlusButton onClick={() => openAddDatasetModal(true)} />
            </Space>
            <Space>
              <Search
                placeholder="search datasets"
                value={searchTerm}
                onChange={handleSearchValueChange}
                onSearch={search}
                loading={isSearching}
                style={{ width: 200 }}
              />
            </Space>
          </div>
        )}
        <Skeleton loading={loading}>
          {searchTerm && searchResults ? (
            <Table
              columns={columns}
              dataSource={searchResults}
              rowKey="datasetId"
              locale={{
                emptyText: "No data matching your search term " + searchTerm,
              }}
            />
          ) : (
            <Table columns={columns} dataSource={datasets} rowKey="datasetId" />
          )}
        </Skeleton>
        <AddDatasetModal
          open={addDatasetModalOpen}
          onCancel={() => openAddDatasetModal(false)}
          onSubmit={addDataset}
        />
      </Space>
    </ListPageLayout>
  );
}

function AddDatasetModal({ open = false, onCancel, onSubmit }) {
  const [form] = Form.useForm();
  return (
    <Modal
      title="Add Dataset"
      open={open}
      onCancel={onCancel}
      okText="Add Dataset"
      afterOpenChange={(open) => {
        if (!open) {
          form.resetFields();
        }
      }}
      onOk={() => {
        form
          .validateFields()
          .then((values) => {
            onSubmit(values);
            form.resetFields();
          })
          .catch((info) => {
            // console.error("validation failed:", info);
          });
      }}
    >
      <Form form={form} layout="vertical" initialValues={{ datasetName: "" }}>
        <Form.Item
          label="Dataset Name"
          name="datasetName"
          rules={[{ required: true, message: "Dataset name is required" }]}
        >
          <Input placeholder="Name" />
        </Form.Item>
      </Form>
    </Modal>
  );
}
